
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'

// module components
import ProposalActions from '@/modules/ProposalModule/components/ProposalActions/index.vue'
import ProposalsTableActionsButton from '@/modules/ProposalModule/components/ProposalsTableActionsButton/index.vue'

// realisations
import { proposal } from '@/core/realisations/proposal.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CreateProposalPayload, ProposalT } from '@/core/types/proposal.types'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { ProposalsColumns } from '@/core/constants/columns/Proposals.columns'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'failed-fetch': FailedFetch,
        'proposal-actions': ProposalActions,
        Cheap,
        TableScrollWrapper,
        TheTable,
        ProposalsTableActionsButton,
    },
    setup() {
        const notifications = useNotifications()
        const { findItemIndexById } = useHelpers()
        const state = reactive(new DataState<ProposalT>({}))

        const loadProposal = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const response = await proposal.getProposal({ page })
                state.list = response.data

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadProposal(1)
        })

        const onProposalClosed = (proposal_id: number) => {
            const index = findItemIndexById(proposal_id, state.list)
            if (index < 0) return

            state.list[index].processed_at = 'some data'
        }

        const onProposalDeleted = () => {
            loadProposal(state.pagination.current_page)
        }

        const { actions_state, startCreate, startUpdate } = useActions<ProposalT>()

        const createProposal = async (payload: CreateProposalPayload) => {
            try {
                actions_state.is_actions_loading = true

                await proposal.createProposal(payload)
                notifications.notify({ title: 'Заявка добавлена', type: 'success' })

                await loadProposal(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При добавлении заявки произошла ошибка',
                    error,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateProposal = async ({
            id,
            payload,
        }: {
            id: number
            payload: CreateProposalPayload
        }) => {
            try {
                actions_state.is_actions_loading = true

                await proposal.updateProposal(id, payload)
                notifications.notify({ title: 'Заявка сохранена', type: 'success' })

                await loadProposal(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При сохранении заявки произошла ошибка',
                    error,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        return {
            state,
            loadProposal,
            onProposalClosed,
            onProposalDeleted,
            actions_state,
            startCreate,
            startUpdate,
            createProposal,
            updateProposal,
            //
            ProposalsColumns,
        }
    },
})
